/**
 * @fileoverview A aparência da esteira de cadastro (etapa 5 dos planos), com os
 * mesmos tons das telas de entrada (AuthLayout): neutros, contraste real no
 * claro, anel de foco de 1px e cor só para o estado (conectado, esperando,
 * desconectado). O formulário (título, campos, botões) é o das telas de
 * entrada (useAuthFormStyles).
 */

import { makeStyles } from "@material-ui/core/styles";

const useSetupStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const faint = "#71717A";
  const line = dark ? "rgba(255,255,255,0.08)" : "#E4E4E7";
  const strongLine = dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)";
  const tint = dark ? "rgba(255,255,255,0.05)" : "#F4F4F5";
  const ok = dark ? "#4ADE80" : "#15803D";
  const warn = dark ? "#FBBF24" : "#B45309";
  const bad = dark ? "#FCA5A5" : "#B91C1C";
  const focus = dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  const reduced = "@media (prefers-reduced-motion: reduce)";
  // Fora da tela, mas lido pelo leitor de tela.
  const hiddenLabel = {
    position: "absolute", width: 1, height: 1, margin: -1, padding: 0, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
  };
  const onlyCurrentLabel = { "& $progressItem:not($progressCurrent) $progressLabel": hiddenLabel };
  return {
    // As etapas da esteira, numa linha só: a feita com o check, a atual em destaque. A linha
    // entre elas encolhe quando falta espaço; no celular, só a atual leva o nome.
    progress: {
      listStyle: "none", margin: "0 0 28px", padding: 0, display: "flex", alignItems: "center", maxWidth: 600,
      [theme.breakpoints.down("xs")]: onlyCurrentLabel,
    },
    progressCompact: onlyCurrentLabel,
    progressItem: {
      display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: faint, whiteSpace: "nowrap",
      "& + &": { flex: "1 1 auto" },
      "& + &::before": { content: '""', flex: "1 1 auto", minWidth: 10, height: 1, background: line, marginLeft: 8 },
    },
    progressMark: {
      width: 20, height: 20, borderRadius: "50%", flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 11, fontWeight: 600, border: `1px solid ${strongLine}`, color: faint, boxSizing: "border-box",
      transition: "background 200ms, color 200ms, border-color 200ms", [reduced]: { transition: "none" },
    },
    progressLabel: {},
    progressDone: { color: muted, "& $progressMark": { background: tint, borderColor: "transparent", color: text } },
    progressCurrent: {
      color: text, fontWeight: 600,
      "& $progressMark": { background: text, borderColor: text, color: dark ? "#0A0A0A" : "#FFFFFF" },
    },

    // A página dos passos depois da conta (dentro do app), como a do assistente de IA.
    page: {
      flex: 1, overflowY: "auto", padding: "36px 48px 56px", background: dark ? "#0A0A0A" : "#FFFFFF", color: text,
      [theme.breakpoints.down("sm")]: { padding: "24px 16px 112px" },
    },
    inner: { maxWidth: 1040, margin: "0 auto" },
    step: { maxWidth: 600 },
    loading: { display: "flex", justifyContent: "center", padding: "64px 0", color: muted },
    labelRow: { display: "flex", alignItems: "center", gap: 6, marginBottom: 6 },
    labelInRow: { marginBottom: 0 },
    info: {
      display: "inline-flex", alignItems: "center", justifyContent: "center", width: 22, height: 22, padding: 0, border: "none",
      borderRadius: 6, background: "transparent", color: faint, cursor: "help",
      "& svg": { width: 15, height: 15 },
      "&:hover": { color: text },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 1 },
    },
    tooltip: {
      fontSize: 12.5, lineHeight: 1.45, maxWidth: 260, padding: "8px 10px", borderRadius: 8, color: text,
      background: dark ? "#1C1C1F" : "#FFFFFF", border: `1px solid ${line}`,
      boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.12)",
    },
    actions: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 24 },
    actionButton: { width: "auto", minWidth: 160, padding: "0 22px" },
    actionSecondary: { width: "auto", marginTop: 0, padding: "0 20px", height: 46 },
    links: { marginTop: 22, display: "flex", gap: 20, flexWrap: "wrap" },

    // Conexões e convites: uma lista simples, com o estado em texto e uma marca pequena.
    list: { listStyle: "none", margin: "20px 0 0", padding: 0, border: `1px solid ${line}`, borderRadius: 12 },
    listItem: {
      display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", minHeight: 52, boxSizing: "border-box",
      "& + &": { borderTop: `1px solid ${line}` },
    },
    listMain: { flex: 1, minWidth: 0 },
    listTitle: { fontSize: 14.5, fontWeight: 500, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    listMeta: { fontSize: 12.5, color: muted, marginTop: 2 },
    status: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 500, whiteSpace: "nowrap" },
    statusDot: { width: 7, height: 7, borderRadius: "50%", background: "currentColor", flexShrink: 0 },
    statusOk: { color: ok },
    statusWait: { color: warn },
    statusBad: { color: bad },
    rowAction: {
      height: 34, padding: "0 12px", borderRadius: 8, border: `1px solid ${strongLine}`, background: "transparent", color: text,
      fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap",
      "&:hover": { background: tint },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
    howTo: { fontSize: 13, color: muted, lineHeight: 1.5, marginTop: 14 },

    // O convite: e-mail e perfil na mesma linha (um embaixo do outro no celular).
    inviteRow: {
      display: "grid", gridTemplateColumns: "minmax(0, 1fr) 180px auto", gap: 10, alignItems: "end",
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    inviteButton: { width: "auto", padding: "0 20px", height: 44 },
    select: { "& .MuiOutlinedInput-root": { borderRadius: 10, height: 44, fontSize: 15 } },

    // O resumo do fim: o que foi feito e o que ficou para depois.
    summary: { margin: "8px 0 0", padding: 0, border: `1px solid ${line}`, borderRadius: 14 },
    summaryRow: {
      display: "grid", gridTemplateColumns: "24px 120px minmax(0, 1fr)", alignItems: "center", gap: 12, padding: "14px 16px",
      "& + &": { borderTop: `1px solid ${line}` },
      animation: "$rowIn 260ms ease-out both", [reduced]: { animation: "none" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "24px minmax(0, 1fr)", "& $summaryValue": { gridColumn: "2" } },
    },
    summaryMark: {
      width: 22, height: 22, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: `1px solid ${strongLine}`, color: faint, boxSizing: "border-box",
    },
    summaryMarkDone: { background: text, borderColor: text, color: dark ? "#0A0A0A" : "#FFFFFF" },
    summaryLabel: { margin: 0, fontSize: 13, color: muted },
    summaryValue: { margin: 0, fontSize: 14.5, fontWeight: 500, color: text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    summaryPending: { color: muted, fontWeight: 400 },
    "@keyframes rowIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
  };
});

export default useSetupStyles;
